import React, { useEffect, useState } from "react";
import favouriteService from "../../services/favourite.service"; 
import "./FavouritePage.css";

// --- Inline SVGs for the new action buttons ---
const EyeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
);

const TrashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
);

const FavouritePage = ({ onNavigate, onAddToCart }) => {
  const [favourites, setFavourites] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [bulkAction, setBulkAction] = useState("Remove");
  
  // UX Loading States
  const [addingItemId, setAddingItemId] = useState(null); 
  const [isAddingBulk, setIsAddingBulk] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const loadFavourites = async () => {
    setIsLoading(true);
    try {
      const data = await favouriteService.getFavourites();
      if (data && !data.error) {
        const favsWithQuantity = data.map(item => ({ ...item, quantity: 1 }));
        setFavourites(favsWithQuantity);
      }
    } catch (error) {
      console.error("Failed to load wishlist:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadFavourites();
  }, []);

  const removeFavourite = async (id) => {
    const previousFavs = [...favourites];
    setFavourites(favourites.filter(item => item.id !== id));
    
    try {
      await favouriteService.removeFavourite(id);
      window.dispatchEvent(new Event("favouritesChanged"));
      setSelectedItems(selectedItems.filter(itemId => itemId !== id));
    } catch (error) {
      console.error("Failed to remove favourite", error);
      setFavourites(previousFavs); 
    }
  };

  const updateQuantity = (id, delta) => {
    setFavourites(favourites.map(item => {
      if (item.id === id) {
        const newQty = Math.max(1, item.quantity + delta);
        return { ...item, quantity: newQty };
      }
      return item;
    }));
  };

  const toggleSelect = (id) => {
    if (selectedItems.includes(id)) {
      setSelectedItems(selectedItems.filter(itemId => itemId !== id));
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  };

  const toggleSelectAll = () => {
    if (selectedItems.length === favourites.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(favourites.map(item => item.id));
    }
  };

  const handleAddToCart = async (item, isBulk = false) => {
    if (onAddToCart) {
      try {
        if (!isBulk) setAddingItemId(item.id); 

        const productForCart = {
          product_id: item.id,
          name: item.name,
          images: [item.image], 
          current_price: item.price,
        };

        await onAddToCart(productForCart, item.quantity);
      } finally {
        if (!isBulk) setAddingItemId(null); 
      }
    }
  };

  const handleBulkApply = async () => {
    if (bulkAction === "Remove" && selectedItems.length > 0) {
      const previousFavs = [...favourites];
      setFavourites(favourites.filter(item => !selectedItems.includes(item.id)));
      
      try {
        await Promise.all(selectedItems.map(id => favouriteService.removeFavourite(id)));
        setSelectedItems([]);
        window.dispatchEvent(new Event("favouritesChanged"));
      } catch (error) {
        console.error("Bulk remove failed", error);
        setFavourites(previousFavs);
      }
    }
  };

  const handleAddAllSelectedToCart = async () => {
    const itemsToAdd = selectedItems.length > 0 
      ? favourites.filter(item => selectedItems.includes(item.id)) 
      : favourites;

    if (itemsToAdd.length === 0) return;

    try {
      setIsAddingBulk(true); 

      for (const item of itemsToAdd) {
        await handleAddToCart(item, true); 
      }

      setSelectedItems([]);
      
      // Dynamic success message
      setSuccessMessage(`✓ ${itemsToAdd.length} products added!`);
      setTimeout(() => {
        setSuccessMessage("");
      }, 4000);

    } finally {
      setIsAddingBulk(false); 
    }
  };

  if (isLoading) {
    return (
      <div className="fav-empty">
        <div className="spinner" style={{ margin: "0 auto 20px auto" }}></div>
        <h2>Loading your wishlist...</h2>
      </div>
    );
  }

  if (favourites.length === 0) {
    return (
      <div className="fav-empty">
        <h2>Your Wishlist is Empty ❤️</h2>
      </div>
    );
  }

  return (
    <div className="wishlist-page">
      <h1 className="wishlist-title">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '10px'}}><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>
        WISHLIST
      </h1>

      <div className="wishlist-table">
        <div className="wishlist-header hidden-mobile">
          <div className="col-checkbox">
            <input 
              type="checkbox" 
              checked={selectedItems.length === favourites.length && favourites.length > 0}
              onChange={toggleSelectAll}
            />
          </div>
          <div className="col-product">PRODUCT</div>
          <div className="col-quantity">QUANTITY</div>
          <div className="col-price">PRICE</div>
          <div className="col-stock">STOCK STATUS</div>
          <div className="col-action">ACTION</div>
        </div>

        <div className="wishlist-body">
          {favourites.map((product) => (
            <div className="wishlist-row" key={product.id}>
              <div className="col-checkbox">
                <input 
                  type="checkbox" 
                  checked={selectedItems.includes(product.id)}
                  onChange={() => toggleSelect(product.id)}
                />
              </div>

              <div className="col-product">
                <img src={product.image} alt={product.name} className="product-thumb" />
                <div className="product-details">
                  <h3>{product.name}</h3>
                  <p>SKU: {product.sku}</p>
                </div>
              </div>

              <div className="col-quantity">
                <div className="qty-selector">
                  <button onClick={() => updateQuantity(product.id, -1)}>-</button>
                  <span>{product.quantity}</span>
                  <button onClick={() => updateQuantity(product.id, 1)}>+</button>
                </div>
              </div>

              <div className="col-price">
                {product.oldPrice && <span className="old-price">₹{product.oldPrice}</span>}
                <span className="current-price">₹{product.price}</span>
              </div>

              <div className="col-stock">
                {product.stock > 0 ? (
                  <span className="stock-in">
                    ✓ {product.stock} in stock
                  </span>
                ) : (
                  <span className="stock-out">
                    ✗ Out of stock
                  </span>
                )}
              </div>

              <div className="col-action">
                <button 
                  className="icon-btn" 
                  title="View Product"
                  onClick={() => onNavigate && onNavigate('product', product.id)}
                >
                  <EyeIcon />
                </button>
                
                <button 
                  className="add-cart-btn" 
                  onClick={() => handleAddToCart(product)}
                  disabled={addingItemId === product.id}
                  style={{ opacity: addingItemId === product.id ? 0.6 : 1, cursor: addingItemId === product.id ? 'wait' : 'pointer' }}
                >
                  {addingItemId === product.id ? "ADDING..." : "ADD TO CART"}
                </button>

                <button className="icon-btn remove" title="Remove" onClick={() => removeFavourite(product.id)}>
                  <TrashIcon />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="wishlist-footer">
        <div className="footer-left">
          <select 
            className="bulk-action-select"
            value={bulkAction}
            onChange={(e) => setBulkAction(e.target.value)}
          >
            <option value="Remove">Remove</option>
          </select>
          <button 
            className="apply-btn" 
            onClick={handleBulkApply}
            disabled={selectedItems.length === 0}
            style={{ opacity: selectedItems.length === 0 ? 0.5 : 1, cursor: selectedItems.length === 0 ? 'not-allowed' : 'pointer' }}
          >
            APPLY
          </button>
        </div>

        {/* 👈 UPDATED FOOTER RIGHT (Message appears inline here!) */}
        <div className="footer-right">
          
          {successMessage && (
            <span className="inline-success-msg">
              {successMessage}
            </span>
          )}

          <button className="estimate-btn" onClick={() => onNavigate('contact')}>✉ ASK FOR AN ESTIMATE</button>
          
          <button 
            className="add-all-btn" 
            onClick={handleAddAllSelectedToCart}
            disabled={isAddingBulk}
            style={{ opacity: isAddingBulk ? 0.7 : 1, cursor: isAddingBulk ? 'wait' : 'pointer' }}
          >
            {isAddingBulk 
              ? "ADDING TO CART..." 
              : (selectedItems.length > 0 ? "ADD SELECTED TO CART" : "ADD ALL TO CART")
            }
          </button>
        </div>
      </div>
    </div>
  );
};

export default FavouritePage;